import React, { useCallback, useEffect, useRef, useState } from "react";

interface HorizontalScrollAreaProps {
  scrollAreaId: string;
  children: React.ReactNode;
}

export function HorizontalScrollArea({ scrollAreaId, children }: HorizontalScrollAreaProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollbarTrackRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ startX: number; startThumbLeft: number } | null>(null);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [isThumbDragging, setIsThumbDragging] = useState(false);

  const updateScrollbar = useCallback(() => {
    const scrollEl = scrollRef.current;
    const trackEl = scrollbarTrackRef.current;
    if (!scrollEl || !trackEl) {
      return;
    }

    const { scrollWidth, clientWidth, scrollLeft } = scrollEl;
    const trackWidth = trackEl.clientWidth;
    if (scrollWidth <= clientWidth) {
      setThumbWidth(0);
      setThumbLeft(0);
      return;
    }

    const nextThumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 32);
    const maxThumbLeft = trackWidth - nextThumbWidth;
    const nextThumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * maxThumbLeft;
    setThumbWidth(nextThumbWidth);
    setThumbLeft(nextThumbLeft);
  }, []);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }

    updateScrollbar();
    const handleScroll = () => updateScrollbar();
    scrollEl.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", updateScrollbar);
    const observer = new ResizeObserver(updateScrollbar);
    observer.observe(scrollEl);
    if (scrollEl.firstElementChild) observer.observe(scrollEl.firstElementChild);
    return () => {
      observer.disconnect();
      scrollEl.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateScrollbar);
    };
  }, [updateScrollbar]);

  useEffect(() => {
    updateScrollbar();
  });

  useEffect(() => {
    if (!isThumbDragging) {
      return;
    }

    const scrollEl = scrollRef.current;
    const trackEl = scrollbarTrackRef.current;
    if (!scrollEl || !trackEl) {
      return;
    }

    const trackWidth = trackEl.clientWidth;
    const maxThumbLeft = trackWidth - thumbWidth;
    const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;

    const handlePointerMove = (event: PointerEvent) => {
      if (!dragStateRef.current) {
        return;
      }

      const { startX, startThumbLeft } = dragStateRef.current;
      const deltaX = event.clientX - startX;
      const nextThumbLeft = Math.min(Math.max(startThumbLeft + deltaX, 0), maxThumbLeft);
      let nextScrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;
      if (Number.isNaN(nextScrollLeft)) {
        nextScrollLeft = 0;
      }

      scrollEl.scrollLeft = nextScrollLeft;
    };

    const handlePointerUp = () => {
      setIsThumbDragging(false);
      dragStateRef.current = null;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isThumbDragging, thumbWidth]);

  return (
    <div className="min-w-0">
      <div ref={scrollRef} tabIndex={thumbWidth > 0 ? 0 : undefined} role="region" aria-label="Analysis table, scroll horizontally" className="overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div id={scrollAreaId} className="w-full">
          {children}
        </div>
      </div>
      <div className={thumbWidth > 0 ? "mt-1 w-full" : "w-full"}>
        <div
          ref={scrollbarTrackRef}
          className="relative h-2 rounded bg-primary-dark/10 dark:bg-white/10"
          style={{ userSelect: "none", height: thumbWidth > 0 ? "8px" : "0px" }}
          onPointerDown={event => {
            const scrollEl = scrollRef.current;
            const trackEl = scrollbarTrackRef.current;
            if (!scrollEl || !trackEl) {
              return;
            }

            const rect = trackEl.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const trackWidth = trackEl.clientWidth;
            const maxThumbLeft = trackWidth - thumbWidth;
            const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
            if (maxScrollLeft <= 0 || maxThumbLeft <= 0) {
              return;
            }

            const nextThumbLeft = Math.max(0, Math.min(clickX - (thumbWidth / 2), maxThumbLeft));
            scrollEl.scrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;
          }}
        >
          {thumbWidth > 0 && (
            <div
              role="scrollbar"
              aria-label="Horizontal scroll handle"
              aria-orientation="horizontal"
              aria-valuemin={0}
              aria-valuemax={100}
              onKeyDown={event => {
                const el = scrollRef.current;
                if (!el) return;
                const delta = event.key === "ArrowRight" ? 80 : event.key === "ArrowLeft" ? -80 : 0;
                if (delta || event.key === "Home" || event.key === "End") {
                  event.preventDefault();
                  el.scrollLeft = event.key === "Home" ? 0 : event.key === "End" ? el.scrollWidth : el.scrollLeft + delta;
                }
              }}
              aria-controls={scrollAreaId}
              aria-valuenow={Math.round((thumbLeft / Math.max(1, (scrollbarTrackRef.current?.clientWidth || 1) - thumbWidth)) * 100)}
              tabIndex={0}
              className="absolute top-0 h-2 touch-none cursor-grab rounded bg-primary-dark/40 dark:bg-primary-light/40 hover:bg-primary-dark/60 dark:hover:bg-primary-light/60 active:cursor-grabbing"
              style={{ width: `${thumbWidth}px`, transform: `translateX(${thumbLeft}px)`, height: thumbWidth > 0 ? "8px" : "0px" }}
              onPointerDown={event => {
                event.preventDefault();
                event.stopPropagation();
                dragStateRef.current = {
                  startX: event.clientX,
                  startThumbLeft: thumbLeft,
                };
                setIsThumbDragging(true);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
